/**
 * Perks (Design pass 1, step 6): at training ranks 3, 6 and 9 of a skill a character picks one of two perks.
 * Definitions and balance only; the effects live at their hooks in packages/sim (see sim/src/perks.ts).
 */
import type { AttributeKey } from './types';

export type PerkTier = 3 | 6 | 9;
export const PERK_TIERS: readonly PerkTier[] = [3, 6, 9];

export type PerkKey =
  | 'drillmaster' | 'quartermaster' | 'tactician' | 'siegecraft' | 'warlord' | 'iron_discipline'
  | 'duellist' | 'hardy' | 'champion' | 'bodyguard' | 'living_legend' | 'undying'
  | 'thrifty' | 'tax_collector' | 'planner' | 'merchant_prince' | 'administrator' | 'treasurer_of_the_realm'
  | 'charming' | 'envoy' | 'matchmaker' | 'peacemaker' | 'kingmaker' | 'grand_diplomat'
  | 'shadow' | 'informant' | 'poisoner' | 'blackmailer' | 'spymaster_general' | 'puppeteer'
  | 'physicians_hand' | 'scholar' | 'mentor' | 'chronicler' | 'sage' | 'reformer';

export interface PerkDef {
  key: PerkKey;
  skill: AttributeKey;
  tier: PerkTier;
  name: string;
  description: string;
}

const P = (key: PerkKey, skill: AttributeKey, tier: PerkTier, name: string, description: string): PerkDef => ({ key, skill, tier, name, description });

/** Two options per (skill, tier), in display order. */
export const PERK_DEFS: Record<PerkKey, PerkDef> = {
  drillmaster: P('drillmaster', 'martial', 3, 'Drillmaster', '+15 % levies raised from the holdings you hold.'),
  quartermaster: P('quartermaster', 'martial', 3, 'Quartermaster', 'Your armies eat 20 % less food.'),
  tactician: P('tactician', 'martial', 6, 'Tactician', 'The army you command deals +10 % casualties in the opening skirmish of a battle.'),
  siegecraft: P('siegecraft', 'martial', 6, 'Siegecraft', 'Sieges laid by your army finish 20 % faster.'),
  warlord: P('warlord', 'martial', 9, 'Warlord', 'The army you command fights with +10 % strength.'),
  iron_discipline: P('iron_discipline', 'martial', 9, 'Iron Discipline', 'Your soldiers do not desert when the pay is late.'),

  duellist: P('duellist', 'prowess', 3, 'Duellist', '+3 Prowess when fighting as a riding knight or army commander.'),
  hardy: P('hardy', 'prowess', 3, 'Hardy', 'Half the chance of being wounded in battle.'),
  champion: P('champion', 'prowess', 6, 'Champion', 'Renown gained from battles is doubled.'),
  bodyguard: P('bodyguard', 'prowess', 6, 'Bodyguard', 'The lord you serve is 0.03 safer against murder and abduction.'),
  living_legend: P('living_legend', 'prowess', 9, 'Living Legend', '+10 renown a year while serving as a knight.'),
  undying: P('undying', 'prowess', 9, 'Undying', 'Survives the first deadly wound taken in battle (once).'),

  thrifty: P('thrifty', 'stewardship', 3, 'Thrifty', '-10 % cost of buildings and development projects.'),
  tax_collector: P('tax_collector', 'stewardship', 3, 'Tax Collector', '+5 % tax from your vassals.'),
  planner: P('planner', 'stewardship', 6, 'Planner', 'Development projects on your holdings finish 25 % sooner.'),
  merchant_prince: P('merchant_prince', 'stewardship', 6, 'Merchant Prince', '+10 % output from your companies.'),
  administrator: P('administrator', 'stewardship', 9, 'Administrator', '+2 demesne limit.'),
  treasurer_of_the_realm: P('treasurer_of_the_realm', 'stewardship', 9, 'Treasurer of the Realm', '+10 % gold from every holding you hold.'),

  charming: P('charming', 'diplomacy', 3, 'Charming', 'Everyone likes you 10 points more.'),
  envoy: P('envoy', 'diplomacy', 3, 'Envoy', 'Your letters travel 50 % faster.'),
  matchmaker: P('matchmaker', 'diplomacy', 6, 'Matchmaker', '+15 acceptance for the marriages you propose.'),
  peacemaker: P('peacemaker', 'diplomacy', 6, 'Peacemaker', 'Truces after your wars last two years longer.'),
  kingmaker: P('kingmaker', 'diplomacy', 9, 'Kingmaker', '+2 vote weight in elections.'),
  grand_diplomat: P('grand_diplomat', 'diplomacy', 9, 'Grand Diplomat', '+10 acceptance for the alliances you propose.'),

  shadow: P('shadow', 'intrigue', 3, 'Shadow', 'Your schemes are 20 % less likely to be discovered.'),
  informant: P('informant', 'intrigue', 3, 'Informant', 'You see schemes against your house before they are discovered.'),
  poisoner: P('poisoner', 'intrigue', 6, 'Poisoner', '+10 % success for your murder schemes.'),
  blackmailer: P('blackmailer', 'intrigue', 6, 'Blackmailer', 'Rivals whose scheme against you is exposed owe you a favour.'),
  spymaster_general: P('spymaster_general', 'intrigue', 9, 'Spymaster General', '0.05 protection against all schemes for your whole house.'),
  puppeteer: P('puppeteer', 'intrigue', 9, 'Puppeteer', 'You may run two schemes at once.'),

  physicians_hand: P('physicians_hand', 'learning', 3, "Physician's Hand", '+1 health for your whole family when illness and age are weighed.'),
  scholar: P('scholar', 'learning', 3, 'Scholar', '+20 % skill XP for your whole family.'),
  mentor: P('mentor', 'learning', 6, 'Mentor', 'Pupils and squires you teach gain +50 % XP.'),
  chronicler: P('chronicler', 'learning', 6, 'Chronicler', '+5 prestige a year.'),
  sage: P('sage', 'learning', 9, 'Sage', 'Pupils you tutor gain +50 % XP when their education ends.'),
  reformer: P('reformer', 'learning', 9, 'Reformer', 'Realm law changes need one council vote less.'),
};

export const PERK_KEYS = Object.keys(PERK_DEFS) as PerkKey[];

/** The two perks offered at (skill, tier), in display order. */
export function perkOptions(skill: AttributeKey, tier: PerkTier): PerkDef[] {
  return PERK_KEYS.map((k) => PERK_DEFS[k]).filter((d) => d.skill === skill && d.tier === tier);
}

// ------------------------------------------------------------------ renown

/** Renown tiers of an adult: Able (75), Renowned/Banneret (150), Great/Champion (400), Legendary (1000). */
export type RenownTier = 'none' | 'able' | 'renowned' | 'great' | 'legendary';
export const RENOWN_ABLE = 75;
export const RENOWN_RENOWNED = 150;
export const RENOWN_GREAT = 400;
export const RENOWN_LEGENDARY = 1000;
export function renownTierOf(renown: number): RenownTier {
  return renown >= RENOWN_LEGENDARY ? 'legendary' : renown >= RENOWN_GREAT ? 'great' : renown >= RENOWN_RENOWNED ? 'renowned'
    : renown >= RENOWN_ABLE ? 'able' : 'none';
}

/** Able and above: the character's liege likes them this much more. */
export const RENOWN_ABLE_LIEGE_OPINION = 5;
/** Great and above: everyone in the same top realm likes them this much more. */
export const RENOWN_GREAT_REALM_OPINION = 10;
/** Renowned and above: extra retinue knight room for a title they hold. */
export const RENOWN_RENOWNED_KNIGHT_BONUS = 1;

// ------------------------------------------------------------------ perk balance
export const PERK_DRILLMASTER_LEVY = 0.15;
export const PERK_QUARTERMASTER_FOOD = 0.2;
export const PERK_TACTICIAN_SKIRMISH = 0.1;
export const PERK_SIEGECRAFT_SPEED = 0.2;
export const PERK_WARLORD_STRENGTH = 0.1;
export const PERK_DUELLIST_PROWESS = 3;
export const PERK_BODYGUARD_DEFENSE = 0.03;
export const PERK_LIVING_LEGEND_RENOWN = 10;
export const PERK_THRIFTY_COST = 0.1;
export const PERK_TAX_COLLECTOR = 0.05;
export const PERK_PLANNER_TIME = 0.25;
export const PERK_MERCHANT_PRINCE = 0.1;
export const PERK_ADMINISTRATOR_DEMESNE = 2;
export const PERK_TREASURER_INCOME = 0.1;
export const PERK_CHARMING_OPINION = 10;
export const PERK_ENVOY_SPEED = 0.5;
export const PERK_MATCHMAKER_ACCEPT = 15;
export const PERK_PEACEMAKER_YEARS = 2;
export const PERK_KINGMAKER_WEIGHT = 2;
export const PERK_GRAND_DIPLOMAT_ACCEPT = 10;
export const PERK_SHADOW_DISCOVERY = 0.2;
export const PERK_POISONER_SUCCESS = 0.1;
export const PERK_SPYMASTER_GENERAL_DEFENSE = 0.05;
export const PERK_PHYSICIAN_HEALTH = 1;
export const PERK_SCHOLAR_XP = 0.2;
export const PERK_MENTOR_XP = 0.5;
export const PERK_CHRONICLER_PRESTIGE = 5;
export const PERK_SAGE_XP = 0.5;
export const PERK_REFORMER_VOTES = 1;
/** Hardy: wound chance multiplier. */
export const PERK_HARDY_WOUND_FACTOR = 0.5;
/** Champion: multiplier on renown (and personal prestige) gained from battles. */
export const PERK_CHAMPION_MULT = 2;
